import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogFooter,
    DialogTitle,
    DialogTrigger,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import {
    Plus,
    Search,
    Filter,
    DollarSign,
    Calendar,
    AlertCircle,
    CheckCircle2,
    MoreVertical,
    Trash2,
    Loader2,
    Repeat
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

export function ExpensesPage() {
    const { t, language } = useLanguage();
    const [expenses, setExpenses] = useState([]);
    const [stats, setStats] = useState(null);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 0 });

    // Filters
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [categoryFilter, setCategoryFilter] = useState('all');

    // Modal State
    const [isAddOpen, setIsAddOpen] = useState(false);
    const [editingExpense, setEditingExpense] = useState(null);
    const [formData, setFormData] = useState({
        description: '',
        amount: '',
        categoryId: '',
        status: 'PENDING',
        vendor: '',
        reference: '',
        notes: '',
        dueDate: '',
        paidDate: '',
        isRecurring: false,
        recurrence: 'MONTHLY'
    });

    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        fetchInitialData();
    }, []);

    useEffect(() => {
        fetchExpenses();
    }, [pagination.page, statusFilter, categoryFilter]);

    const fetchInitialData = async () => {
        try {
            const [categoriesRes, statsRes] = await Promise.all([
                axios.get(`${API_URL}/expense-categories`, { headers }),
                axios.get(`${API_URL}/expenses/stats`, { headers })
            ]);
            setCategories(categoriesRes.data.data);
            setStats(statsRes.data.data);
            fetchExpenses();
        } catch (error) {
            console.error('Failed to fetch initial data:', error);
        }
    };

    const fetchExpenses = async () => {
        setLoading(true);
        try {
            const params = {
                page: pagination.page,
                limit: pagination.limit,
                status: statusFilter !== 'ALL' ? statusFilter : undefined,
                categoryId: categoryFilter !== 'all' ? categoryFilter : undefined,
                search: search || undefined
            };

            const response = await axios.get(`${API_URL}/expenses`, { headers, params });
            setExpenses(response.data.data.expenses);
            setPagination(response.data.data.pagination);
        } catch (error) {
            console.error('Failed to fetch expenses:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate required fields (Select component doesn't support HTML required)
        if (!formData.description || !formData.amount || !formData.categoryId) {
            toast.error(t('fillRequired'));
            return;
        }

        try {
            const payload = {
                ...formData,
                amount: parseFloat(formData.amount),
                isRecurring: Boolean(formData.isRecurring),
                recurrence: formData.isRecurring ? formData.recurrence : null
            };

            if (editingExpense) {
                await axios.put(`${API_URL}/expenses/${editingExpense.id}`, payload, { headers });
            } else {
                await axios.post(`${API_URL}/expenses`, payload, { headers });
            }

            setIsAddOpen(false);
            setEditingExpense(null);
            resetForm();
            fetchExpenses();
            fetchInitialData(); // Refresh stats
        } catch (error) {
            console.error('Failed to save expense:', error);
            toast.error('Failed to save expense. Please try again.');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t('confirmDeleteExpense'))) return;
        try {
            await axios.delete(`${API_URL}/expenses/${id}`, { headers });
            fetchExpenses();
            fetchInitialData();
        } catch (error) {
            console.error('Failed to delete expense:', error);
        }
    };

    const handleMarkPaid = async (id) => {
        try {
            await axios.patch(`${API_URL}/expenses/${id}/pay`, {}, { headers });
            fetchExpenses();
            fetchInitialData();
        } catch (error) {
            console.error('Failed to mark expense as paid:', error);
        }
    };

    const startEdit = (expense) => {
        setEditingExpense(expense);
        setFormData({
            description: expense.description,
            amount: expense.amount,
            categoryId: expense.categoryId,
            status: expense.status,
            vendor: expense.vendor || '',
            reference: expense.reference || '',
            notes: expense.notes || '',
            dueDate: expense.dueDate ? expense.dueDate.split('T')[0] : '',
            paidDate: expense.paidDate ? expense.paidDate.split('T')[0] : '',
            isRecurring: Boolean(expense.isRecurring),
            recurrence: expense.recurrence || 'MONTHLY'
        });
        setIsAddOpen(true);
    };

    const resetForm = () => {
        setFormData({
            description: '',
            amount: '',
            categoryId: '',
            status: 'PENDING',
            vendor: '',
            reference: '',
            notes: '',
            dueDate: '',
            paidDate: '',
            isRecurring: false,
            recurrence: 'MONTHLY'
        });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'PAID': return 'bg-green-500/10 text-green-500 border-green-500/20';
            case 'PENDING': return 'bg-amber-500/10 text-amber-500 border-amber-500/20';
            case 'CANCELLED': return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
            default: return 'bg-gray-500/10 text-gray-500';
        }
    };

    return (
        <Layout>
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold">{t('expenseTracking')}</h1>
                        <p className="text-muted-foreground">{t('expenseTrackingSubtitle')}</p>
                    </div>
                    <div className="flex gap-2">
                        <Button
                            variant="secondary"
                            className="bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/40 hover:text-blue-700 dark:hover:text-blue-300 border border-blue-200 dark:border-blue-800"
                            onClick={() => {
                                setEditingExpense(null);
                                resetForm();
                                setFormData(prev => ({ ...prev, isRecurring: true }));
                                setIsAddOpen(true);
                            }}
                        >
                            <Repeat className="h-4 w-4 mr-2" />
                            Add Recurring Payment
                        </Button>
                        <Button onClick={() => { setEditingExpense(null); resetForm(); setIsAddOpen(true); }}>
                            <Plus className="h-4 w-4 mr-2" />
                            {t('addExpense')}
                        </Button>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between pb-2">
                            <CardTitle className="text-sm font-medium text-muted-foreground">{t('totalPending')}</CardTitle>
                            <AlertCircle className="h-4 w-4 text-amber-500" />
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold text-amber-500">
                                {formatCurrency(stats?.totalPending || 0)}
                            </div>
                            <p className="text-xs text-muted-foreground">{t('unpaidInvoices')}</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between pb-2">
                            <CardTitle className="text-sm font-medium text-muted-foreground">{t('paidLast30Days')}</CardTitle>
                            <CheckCircle2 className="h-4 w-4 text-green-500" />
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold text-green-500">
                                {formatCurrency(stats?.paidLast30Days || 0)}
                            </div>
                            <p className="text-xs text-muted-foreground">{t('settledExpenses')}</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between pb-2">
                            <CardTitle className="text-sm font-medium text-muted-foreground">{t('overdue')}</CardTitle>
                            <AlertCircle className="h-4 w-4 text-destructive" />
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold text-destructive">
                                {stats?.overdueCount || 0}
                            </div>
                            <p className="text-xs text-muted-foreground">{t('pastDueDate')}</p>
                        </CardContent>
                    </Card>
                </div>

                {/* Filters */}
                <Card>
                    <CardContent className="p-4">
                        <div className="flex flex-col md:flex-row gap-4">
                            <div className="flex-1 relative">
                                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                                <Input
                                    placeholder={t('searchExpenses')}
                                    className="pl-8"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && fetchExpenses()}
                                />
                            </div>
                            <Select value={statusFilter} onValueChange={setStatusFilter}>
                                <SelectTrigger className="w-[180px]">
                                    <SelectValue placeholder={t('statusProp')} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="ALL">{t('allStatuses') || 'All Statuses'}</SelectItem>
                                    <SelectItem value="PENDING">{t('pending')}</SelectItem>
                                    <SelectItem value="PAID">{t('paid')}</SelectItem>
                                    <SelectItem value="CANCELLED">{t('cancelled')}</SelectItem>
                                </SelectContent>
                            </Select>
                            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                                <SelectTrigger className="w-[180px]">
                                    <SelectValue placeholder={t('expenseCategory')} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{t('allCategories')}</SelectItem>
                                    {categories.map(cat => (
                                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Button variant="outline" onClick={fetchExpenses}>
                                <Filter className="h-4 w-4 mr-2" />
                                {t('filter') || 'Filter'}
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {/* Expenses Table */}
                <Card>
                    <CardContent className="p-0">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>{t('description')}</TableHead>
                                    <TableHead>{t('expenseCategory')}</TableHead>
                                    <TableHead>{t('statusProp')}</TableHead>
                                    <TableHead>{t('vendor')}</TableHead>
                                    <TableHead>{t('dueDate')}</TableHead>
                                    <TableHead className="text-right">{t('amount')}</TableHead>
                                    <TableHead className="w-[50px]"></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={7} className="h-24 text-center">
                                            <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                                        </TableCell>
                                    </TableRow>
                                ) : expenses.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} className="h-24 text-center text-muted-foreground">
                                            {t('noExpenses')}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    expenses.map((expense) => (
                                        <TableRow key={expense.id}>
                                            <TableCell className="font-medium">
                                                <div className="flex items-center gap-2">
                                                    {expense.description}
                                                    {expense.isRecurring && (
                                                        <Repeat className="h-3 w-3 text-blue-500" title={`Recurring: ${expense.recurrence}`} />
                                                    )}
                                                </div>
                                                {expense.reference && (
                                                    <div className="text-xs text-muted-foreground">Ref: {expense.reference}</div>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant="outline" style={{ borderColor: expense.category?.color, color: expense.category?.color }}>
                                                    {expense.category?.name}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                <Badge className={getStatusColor(expense.status)} variant="outline">
                                                    {t(expense.status.toLowerCase())}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>{expense.vendor || '-'}</TableCell>
                                            <TableCell>
                                                {expense.dueDate ? new Date(expense.dueDate).toLocaleDateString() : '-'}
                                            </TableCell>
                                            <TableCell className="text-right font-medium">
                                                {formatCurrency(expense.amount)}
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    {expense.status === 'PENDING' && (
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="text-green-500 hover:text-green-600 hover:bg-green-50"
                                                            onClick={() => handleMarkPaid(expense.id)}
                                                            title={t('markPaid')}
                                                        >
                                                            <CheckCircle2 className="h-4 w-4" />
                                                        </Button>
                                                    )}
                                                    <Button variant="ghost" size="icon" onClick={() => startEdit(expense)}>
                                                        <MoreVertical className="h-4 w-4" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="text-destructive hover:text-destructive hover:bg-red-50"
                                                        onClick={() => handleDelete(expense.id)}
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>

                {/* Add/Edit Modal */}
                <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
                    <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle>{editingExpense ? t('editExpense') : t('addNewExpense')}</DialogTitle>
                            <DialogDescription>
                                {editingExpense ? t('updateExpenseDetails') : t('registerExpense')}
                            </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4 py-4">
                            <div className="grid gap-2">
                                <Label htmlFor="description">{t('description')} *</Label>
                                <Input
                                    id="description"
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    required
                                />
                            </div>

                            {/* RECURRING PAYMENT SECTION MOVED TO TOP */}
                            <div className="p-3 bg-blue-50/50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-lg space-y-4 mb-2">
                                <div className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        id="isRecurring"
                                        className="h-4 w-4 rounded border-gray-300 dark:border-slate-600 text-blue-600 focus:ring-blue-600"
                                        checked={formData.isRecurring}
                                        onChange={(e) => setFormData({ ...formData, isRecurring: e.target.checked })}
                                    />
                                    <Label htmlFor="isRecurring" className="cursor-pointer font-medium text-blue-900 dark:text-blue-100">
                                        This is a Recurring Payment
                                    </Label>
                                </div>
                                {formData.isRecurring && (
                                    <div className="grid gap-2 pl-6">
                                        <Label htmlFor="recurrence" className="text-blue-800 dark:text-blue-300">{t('recurrenceInterval') || 'Payment Interval'} *</Label>
                                        <Select
                                            value={formData.recurrence}
                                            onValueChange={(val) => setFormData({ ...formData, recurrence: val })}
                                        >
                                            <SelectTrigger className="border-blue-200 dark:border-blue-800 bg-white dark:bg-background">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="WEEKLY">{t('weekly') || 'Weekly'}</SelectItem>
                                                <SelectItem value="MONTHLY">{t('monthly') || 'Monthly'}</SelectItem>
                                                <SelectItem value="QUARTERLY">{t('quarterly') || 'Quarterly'}</SelectItem>
                                                <SelectItem value="YEARLY">{t('yearly') || 'Yearly'}</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                                            A new pending expense will be automatically generated whenever the current one is marked as Paid.
                                        </p>
                                    </div>
                                )}
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="grid gap-2">
                                    <Label htmlFor="amount">{t('amount')} *</Label>
                                    <div className="relative">
                                        <DollarSign className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                                        <Input
                                            id="amount"
                                            className="pl-8"
                                            type="number"
                                            step="0.01"
                                            value={formData.amount}
                                            onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="grid gap-2">
                                    <Label htmlFor="category">{t('expenseCategory')} *</Label>
                                    <Select
                                        value={formData.categoryId}
                                        onValueChange={(val) => setFormData({ ...formData, categoryId: val })}
                                        required
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder={t('select') || 'Select'} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {categories.map(cat => (
                                                <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="grid gap-2">
                                    <Label htmlFor="vendor">{t('vendor')}</Label>
                                    <Input
                                        id="vendor"
                                        value={formData.vendor}
                                        onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
                                    />
                                </div>
                                <div className="grid gap-2">
                                    <Label htmlFor="reference">{t('reference')}</Label>
                                    <Input
                                        id="reference"
                                        value={formData.reference}
                                        onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="grid gap-2">
                                    <Label htmlFor="status">{t('statusProp')}</Label>
                                    <Select
                                        value={formData.status}
                                        onValueChange={(val) => setFormData({ ...formData, status: val })}
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="PENDING">{t('pending')}</SelectItem>
                                            <SelectItem value="PAID">{t('paid')}</SelectItem>
                                            <SelectItem value="CANCELLED">{t('cancelled')}</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="grid gap-2">
                                    <Label htmlFor="dueDate">{t('dueDate')}</Label>
                                    <Input
                                        id="dueDate"
                                        type="date"
                                        value={formData.dueDate}
                                        onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                                    />
                                </div>
                            </div>
                            {/* Original recurrence section removed from here */}
                            <div className="grid gap-2">
                                <Label htmlFor="notes">{t('notes')}</Label>
                                <Input
                                    id="notes"
                                    value={formData.notes}
                                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                />
                            </div>
                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => setIsAddOpen(false)}>
                                    {t('cancel') || 'Cancel'}
                                </Button>
                                <Button type="submit">
                                    {editingExpense ? t('updateExpense') : t('createExpense')}
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>
        </Layout>
    );
}
